import { Box, CircularProgress, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type PDFSource = {
  objectUrl: string | null;
  reason: 'base64' | 'https' | 'dataurl' | 'blob' | 'unsupported';
  fileName?: string;
};

interface PDFViewerProps {
  url?: string;
  width?: string | number;
  height?: string | number;
  onError?: (error: Error) => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ url, width = '100%', height = '100%', onError }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string | null>(null);
  const [src, setSrc] = useState<PDFSource>({
    objectUrl: null,
    reason: 'unsupported',
  });
  const lastObjectUrlRef = useRef<string | null>(null);
  const { t } = useTranslation();

  const base64ToBlob = (b64: string, mime = 'application/pdf') => {
    const byteChars = atob(b64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  const revokePrev = () => {
    if (lastObjectUrlRef.current) {
      URL.revokeObjectURL(lastObjectUrlRef.current);
      lastObjectUrlRef.current = null;
    }
  };

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    revokePrev();

    try {
      if (url.startsWith('data:application/pdf;base64,')) {
        const base64 = url.split(',')[1] ?? '';
        const blob = base64ToBlob(base64);
        const objectUrl = URL.createObjectURL(blob);
        lastObjectUrlRef.current = objectUrl;
        setSrc({ objectUrl, reason: 'dataurl' });
        setLoading(false);
        return;
      }

      if (url.startsWith('http://') || url.startsWith('https://')) {
        setSrc({ objectUrl: url, reason: 'https' });
        setLoading(false);
        return;
      }

      if (url.startsWith('blob:')) {
        setSrc({ objectUrl: url, reason: 'blob' });
        setLoading(false);
        return;
      }

      if (url.startsWith('file://')) {
        setErr(t('mobileTransactionDetails.notSupportedFile'));
        setSrc({ objectUrl: null, reason: 'unsupported' });
        setLoading(false);
        return;
      }

      setErr(t('mobileTransactionDetails.notSupportedFilePDF'));
      setSrc({ objectUrl: null, reason: 'unsupported' });
      setLoading(false);
    } catch (e: any) {
      const message = e?.message ?? t('mobileTransactionDetails.notSupportedFilePDF');
      setErr(message);
      setSrc({ objectUrl: null, reason: 'unsupported' });
      setLoading(false);
      onError?.(new Error(message));
      captureException(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    const handler = (e: any) => {
      try {
        const files = e?.detail?.files as Array<any> | undefined;
        if (!files?.length) return;

        const pdf = files.find(
          (f) =>
            (f?.type === 'application/pdf' ||
              String(f?.fileName || '')
                .toLowerCase()
                .endsWith('.pdf')) &&
            !!f?.base64
        );
        if (!pdf) return;

        setLoading(true);
        setErr(null);
        revokePrev();

        const blob = base64ToBlob(pdf.base64, 'application/pdf');
        const objectUrl = URL.createObjectURL(blob);
        lastObjectUrlRef.current = objectUrl;
        setSrc({ objectUrl, reason: 'base64', fileName: pdf.fileName });
        setLoading(false);
      } catch (e: any) {
        const message = e?.message ?? t('mobileTransactionDetails.errorReadingPDF');
        setErr(message);
        setLoading(false);
        onError?.(new Error(message));
        captureException(e);
      }
    };

    window.addEventListener('FILES_FROM_NATIVE', handler as EventListener);
    return () => {
      window.removeEventListener('FILES_FROM_NATIVE', handler as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => revokePrev();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width,
          height,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="caption" sx={{ mt: 1 }}>
          {t('mobileTransactionDetails.loadingPDF')}
        </Typography>
      </Box>
    );
  }

  if (err || !src.objectUrl) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="error">
          {err ?? t('mobileTransactionDetails.notSupportedFilePDF')}
        </Typography>
        {url?.startsWith('file://') && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            {t('mobileTransactionDetails.notSupportedFileFormat')}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width, height, position: 'relative' }}>
      <Box
        component="iframe"
        src={`${src.objectUrl}#toolbar=0&navpanes=0&scrollbar=0`}
        title={src.fileName || 'PDF Preview'}
        sx={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: 1,
          backgroundColor: 'white',
        }}
        onError={(_e: React.SyntheticEvent<HTMLIFrameElement>) => {
          onError?.(new Error('Failed to load PDF in iframe'));
        }}
      />
    </Box>
  );
};

export default PDFViewer;
